import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Alert, Spinner, Modal } from 'react-bootstrap';
import { useGetCrmCalendarSourcesQuery, useLazyGetCrmCalendarQuery } from '../../app/api';
import { useIsMobile } from '../utils';

// Work -> CRM Calendar: a merging firm's office calendar, read live from its
// Redtail database (Hammer first). Every active Redtail user's activities -
// appointments, calls, tasks - on one month grid, filterable by staff member
// and activity type. Read-only; nothing here writes to Redtail.
//
// Redtail is slow (~15s for a month), so the server caches each window for a
// few minutes and Refresh bypasses that. Recurring series arrive already
// expanded into the window, with first_day / last_day as local dates, so the
// browser does date maths on 'YYYY-MM-DD' strings only (same approach as the
// Upcoming Events page).

const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  canvas:    '#f6f5f2',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CHIPS = 3;
const SOURCE_KEY = 'crmCalendar.source';

const cardStyle = {
  background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden',
};
const selectStyle = {
  padding: '0.4rem 0.6rem', border: `1px solid ${work.hairline}`, borderRadius: 8,
  fontSize: '0.85rem', color: work.deepBlue, background: 'white', maxWidth: '100%',
};
const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };

function btn(bg, fg) {
  return {
    background: bg, color: fg, border: 'none', borderRadius: 8,
    padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.85rem',
  };
}

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

function localToday() {
  const now = new Date();
  return iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

/** Every cell of a Sunday-first month grid, as whole weeks. */
function monthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  const start = addDays(first, -first.getUTCDay());
  const end = addDays(last, 6 - last.getUTCDay());
  const cells = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    cells.push({ key: iso(d), date: d.getUTCDate(), inMonth: d.getUTCMonth() === month });
  }
  return { cells, start: iso(start), end: iso(end) };
}

function monthTitle(year, month) {
  return new Date(Date.UTC(year, month, 1))
    .toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function dayTitle(key) {
  return new Date(`${key}T00:00:00Z`)
    .toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

function readStored() {
  try { return window.localStorage.getItem(SOURCE_KEY) || ''; } catch (_e) { return ''; }
}
function store(v) {
  try { window.localStorage.setItem(SOURCE_KEY, v); } catch (_e) { /* storage unavailable */ }
}

function Chip({ ev }) {
  return (
    <div
      title={`${ev.subject} (${ev.time_label})`}
      style={{
        fontSize: '0.72rem', lineHeight: 1.3, padding: '1px 5px', marginTop: 2, borderRadius: 4,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        background: ev.is_all_day ? 'rgba(0,105,166,0.1)' : 'transparent',
        color: work.deepBlue, fontWeight: 500,
        opacity: ev.completed ? 0.55 : 1,
        textDecoration: ev.completed ? 'line-through' : 'none',
      }}
    >
      {!ev.is_all_day && ev.start_label && (
        <strong style={{ color: work.tealBlue, marginRight: 3 }}>{ev.start_label.replace(':00', '')}</strong>
      )}
      {ev.subject}
    </div>
  );
}
Chip.propTypes = { ev: PropTypes.object.isRequired };

function DayCell({ cell, events, isToday, selected, compact, onSelect }) {
  const shown = events.slice(0, compact ? 0 : MAX_CHIPS);
  const more = events.length - shown.length;
  return (
    <button
      type="button"
      onClick={() => onSelect(cell.key)}
      aria-pressed={selected}
      aria-label={`${dayTitle(cell.key)}, ${events.length ? `${events.length} item${events.length === 1 ? '' : 's'}` : 'nothing scheduled'}`}
      style={{
        display: 'block', width: '100%', minWidth: 0, textAlign: 'left',
        minHeight: compact ? 54 : 104, padding: compact ? '4px 2px' : '4px 5px',
        border: 'none', borderTop: `1px solid ${work.hairline}`, borderLeft: `1px solid ${work.hairline}`,
        background: selected ? 'rgba(208,155,44,0.12)' : (cell.inMonth ? 'white' : '#faf9f7'),
        boxShadow: selected ? `inset 0 0 0 2px ${work.goldenrod}` : 'none',
        cursor: 'pointer',
      }}
    >
      <div style={{ textAlign: compact ? 'center' : 'left' }}>
        <span style={{
          display: 'inline-block', minWidth: 22, height: 22, lineHeight: '22px', textAlign: 'center',
          borderRadius: 11, fontSize: '0.78rem', fontWeight: isToday ? 800 : 600,
          background: isToday ? work.goldenrod : 'transparent',
          color: cell.inMonth ? work.deepBlue : '#a8a6a2',
        }}>
          {cell.date}
        </span>
      </div>
      {shown.map((ev) => <Chip key={ev.id} ev={ev} />)}
      {more > 0 && (compact ? (
        <div style={{ textAlign: 'center', marginTop: 2 }}>
          <span style={{
            display: 'inline-block', minWidth: 18, borderRadius: 9, padding: '0 5px',
            fontSize: '0.68rem', fontWeight: 700, background: work.tealBlue, color: 'white',
          }}>
            {more}
          </span>
        </div>
      ) : (
        <div style={{ fontSize: '0.7rem', color: work.muted, fontWeight: 700, marginTop: 2, paddingLeft: 5 }}>
          +{more} more
        </div>
      ))}
    </button>
  );
}
DayCell.propTypes = {
  cell: PropTypes.object.isRequired,
  events: PropTypes.array.isRequired,
  isToday: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  compact: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function EventDetail({ ev, staffNames, first, stacked }) {
  const who = ev.attendee_ids.map((id) => staffNames[id]).filter(Boolean);
  return (
    <div className={stacked ? '' : 'd-flex gap-3'} style={{ padding: '0.7rem 0', borderTop: first ? 'none' : `1px solid ${work.hairline}` }}>
      <div style={{ flex: '0 0 auto', width: stacked ? 'auto' : 130, fontSize: '0.8rem', fontWeight: 700, color: ev.is_all_day ? work.goldenrod : work.tealBlue }}>
        {ev.time_label}
      </div>
      <div style={{ ...WRAP, flex: 1 }}>
        <div style={{ color: work.deepBlue, fontWeight: 700, textDecoration: ev.completed ? 'line-through' : 'none' }}>
          {ev.subject}
        </div>
        <div style={{ fontSize: '0.78rem', color: work.muted, marginTop: 2 }}>
          {ev.type && (
            <span style={{ background: work.canvas, borderRadius: 4, padding: '0 6px', marginRight: 6, fontWeight: 600 }}>{ev.type}</span>
          )}
          {ev.completed && <span className="me-2"><i className="bi bi-check2-circle me-1" />Completed</span>}
          {ev.recurring && <span className="me-2"><i className="bi bi-arrow-repeat me-1" />Repeats</span>}
          {who.length > 0 && <span><i className="bi bi-person me-1" />{who.join(', ')}</span>}
        </div>
        {ev.contacts.length > 0 && (
          <div style={{ fontSize: '0.82rem', marginTop: 3 }}>
            <i className="bi bi-person-lines-fill me-1" style={{ color: work.muted }} />
            {ev.contacts.map((c, i) => (
              <React.Fragment key={c.contact_id}>
                {i > 0 && ', '}
                {c.client_id
                  ? <Link to={`/work/crm/${c.client_id}`} style={{ color: work.tealBlue }}>{c.name}</Link>
                  : <span style={{ color: work.deepBlue }}>{c.name}</span>}
              </React.Fragment>
            ))}
          </div>
        )}
        {ev.location && (
          <div style={{ fontSize: '0.8rem', color: work.muted, marginTop: 2 }}>
            <i className="bi bi-geo-alt me-1" />{ev.location}
          </div>
        )}
        {ev.description && (
          <div style={{ fontSize: '0.82rem', color: '#4b5563', marginTop: 4, whiteSpace: 'pre-line' }}>
            {ev.description}
          </div>
        )}
      </div>
    </div>
  );
}
EventDetail.propTypes = {
  ev: PropTypes.object.isRequired,
  staffNames: PropTypes.object.isRequired,
  first: PropTypes.bool,
  stacked: PropTypes.bool,
};

export default function CrmCalendar() {
  const isMobile = useIsMobile();
  const { data: srcData, isLoading: srcLoading, isError: srcError } = useGetCrmCalendarSourcesQuery();
  const sources = useMemo(() => srcData?.sources || [], [srcData]);
  const [source, setSource] = useState('');

  // Remembered firm, else Hammer (the first one built), else the first listed.
  useEffect(() => {
    if (!sources.length || sources.some((s) => s.label === source)) return;
    const stored = readStored();
    const pick = sources.find((s) => s.label === stored) || sources.find((s) => s.label === 'hammer') || sources[0];
    setSource(pick.label);
  }, [sources, source]);

  const today0 = localToday();
  const [ym, setYm] = useState(() => ({ y: Number(today0.slice(0, 4)), m: Number(today0.slice(5, 7)) - 1 }));
  const [selected, setSelected] = useState(today0);
  const [openDay, setOpenDay] = useState(null);
  const [staffId, setStaffId] = useState('');
  const [type, setType] = useState('');
  const [hideCompleted, setHideCompleted] = useState(false);
  const grid = useMemo(() => monthGrid(ym.y, ym.m), [ym]);

  const [load, res] = useLazyGetCrmCalendarQuery();
  useEffect(() => {
    if (source) load({ source, start: grid.start, end: grid.end }, true);
  }, [source, grid, load]);

  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load({ source, start: grid.start, end: grid.end, fresh: true }, false);
    } finally {
      setRefreshing(false);
    }
  }, [load, source, grid]);

  const goMonth = (delta) => {
    const d = new Date(Date.UTC(ym.y, ym.m + delta, 1));
    setYm({ y: d.getUTCFullYear(), m: d.getUTCMonth() });
    setSelected(iso(d));
  };
  const goToday = () => {
    const t = localToday();
    setYm({ y: Number(t.slice(0, 4)), m: Number(t.slice(5, 7)) - 1 });
    setSelected(t);
  };
  const pickSource = (v) => {
    setSource(v);
    store(v);
    setStaffId('');
    setType('');
  };

  const data = res.data;
  // Only trust a response for the firm and grid on screen: while another
  // loads, the previous one is still in `data`.
  const current = data && data.source === source && data.start === grid.start && data.end === grid.end ? data : null;
  const today = current?.today || today0;
  const staffNames = useMemo(
    () => Object.fromEntries((current?.staff || []).map((s) => [s.id, s.name])),
    [current],
  );

  const byDay = useMemo(() => {
    const m = {};
    const sid = staffId ? Number(staffId) : null;
    for (const ev of current?.events || []) {
      if (sid && !ev.attendee_ids.includes(sid)) continue;
      if (type && ev.type !== type) continue;
      if (hideCompleted && ev.completed) continue;
      for (let d = new Date(`${ev.first_day}T00:00:00Z`); iso(d) <= ev.last_day; d = addDays(d, 1)) {
        const k = iso(d);
        if (k < grid.start || k > grid.end) continue;
        (m[k] = m[k] || []).push(ev);
      }
    }
    return m;
  }, [current, grid, staffId, type, hideCompleted]);

  const onSelectDay = useCallback((key) => {
    setSelected(key);
    if ((byDay[key] || []).length) setOpenDay(key);
  }, [byDay]);
  const openEvents = (openDay && byDay[openDay]) || [];
  const sourceName = sources.find((s) => s.label === source)?.display_name || '';

  let body;
  if (srcLoading) {
    body = <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  } else if (srcError) {
    body = <Alert variant="warning">The CRM calendars could not be listed just now. Please try again shortly.</Alert>;
  } else if (!sources.length) {
    body = (
      <Alert variant="info" style={{ fontSize: '0.9rem' }}>
        None of the offices you can see has a Redtail calendar connected.
      </Alert>
    );
  } else if (res.isError && !res.isFetching) {
    body = (
      <Alert variant="warning" style={{ fontSize: '0.9rem' }}>
        {res.error?.data?.detail || 'The calendar could not be read just now. Please try again shortly.'}
      </Alert>
    );
  } else {
    const loading = !current || res.isFetching;
    body = (
      <>
        <div style={{ ...cardStyle, position: 'relative' }}>
          <div className="d-flex align-items-center gap-2" style={{ padding: '0.6rem 0.75rem', background: work.deepBlue }}>
            <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month"
              style={{ ...btn('transparent', 'white'), padding: '0.25rem 0.5rem' }}>
              <i className="bi bi-chevron-left" />
            </button>
            <h5 className="mb-0" style={{ color: 'white', fontWeight: 800, flex: 1, textAlign: 'center', fontSize: isMobile ? '1rem' : '1.15rem' }}>
              {monthTitle(ym.y, ym.m)}
            </h5>
            <button type="button" onClick={() => goMonth(1)} aria-label="Next month"
              style={{ ...btn('transparent', 'white'), padding: '0.25rem 0.5rem' }}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderRight: `1px solid ${work.hairline}` }}>
            {WEEKDAYS.map((w) => (
              <div key={w} style={{
                textAlign: 'center', padding: '0.35rem 0', fontSize: '0.72rem', fontWeight: 800,
                letterSpacing: '0.04em', textTransform: 'uppercase', color: work.muted,
                borderLeft: `1px solid ${work.hairline}`, background: work.canvas,
              }}>
                {isMobile ? w.charAt(0) : w}
              </div>
            ))}
            {grid.cells.map((cell) => (
              <DayCell
                key={cell.key}
                cell={cell}
                events={byDay[cell.key] || []}
                isToday={cell.key === today}
                selected={cell.key === selected}
                compact={isMobile}
                onSelect={onSelectDay}
              />
            ))}
          </div>

          {loading && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', background: 'rgba(255,255,255,0.7)', color: work.muted, fontSize: '0.9rem',
              textAlign: 'center', padding: '1rem',
            }}>
              <div><Spinner animation="border" size="sm" className="me-2" />Reading Redtail…</div>
              <div style={{ fontSize: '0.78rem', marginTop: 4 }}>A month takes about 20 seconds the first time.</div>
            </div>
          )}
        </div>

        <Modal show={openEvents.length > 0} onHide={() => setOpenDay(null)} centered scrollable size="lg">
          <Modal.Header closeButton style={{ borderBottom: `3px solid ${work.goldenrod}` }}>
            <Modal.Title style={{ color: work.deepBlue, fontWeight: 800, fontSize: '1.1rem' }}>
              {openDay && dayTitle(openDay)}
              <span style={{ color: work.muted, fontWeight: 600, fontSize: '0.85rem', marginLeft: 8 }}>
                {openEvents.length} item{openEvents.length === 1 ? '' : 's'}
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}>
            {openEvents.map((ev, i) => (
              <EventDetail key={ev.id} ev={ev} staffNames={staffNames} first={i === 0} stacked={isMobile} />
            ))}
          </Modal.Body>
        </Modal>
      </>
    );
  }

  // Staff with something this month, plus whoever is picked, so the filter
  // survives paging into a month they have nothing in.
  const staffOptions = (current?.staff || []).filter((s) => s.n > 0 || String(s.id) === staffId);
  return (
    <Container fluid className="py-3 px-md-4" style={{ maxWidth: 1280 }}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h4 className="mb-0" style={{ color: work.deepBlue, fontWeight: 800 }}>CRM Calendar</h4>
          <div style={{ color: work.muted, fontSize: '0.84rem' }}>
            {sourceName ? `${sourceName}'s office calendar from Redtail` : 'Office calendars from Redtail'}
            {current && ` - times in ${current.timezone_label}`}. Read-only. Click a day for details.
          </div>
        </div>
        {sources.length > 0 && (
          <>
            <button type="button" onClick={goToday} style={btn('#e9e6e1', work.deepBlue)}>Today</button>
            <button type="button" onClick={refresh} disabled={refreshing || res.isFetching}
              style={btn('#e9e6e1', work.deepBlue)}>
              <i className="bi bi-arrow-clockwise me-1" />{refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </>
        )}
      </div>

      {sources.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {sources.length > 1 && (
            <select value={source} onChange={(e) => pickSource(e.target.value)} aria-label="Firm" style={selectStyle}>
              {sources.map((s) => <option key={s.label} value={s.label}>{s.display_name}</option>)}
            </select>
          )}
          <select value={staffId} onChange={(e) => setStaffId(e.target.value)} aria-label="Staff member" style={selectStyle}>
            <option value="">All staff</option>
            {staffOptions.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.n})</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Activity type" style={selectStyle}>
            <option value="">All types</option>
            {(current?.types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="d-flex align-items-center gap-1 mb-0" style={{ fontSize: '0.85rem', color: work.deepBlue }}>
            <input type="checkbox" checked={hideCompleted} onChange={(e) => setHideCompleted(e.target.checked)} />
            Hide completed
          </label>
        </div>
      )}
      {body}
    </Container>
  );
}
